<!-- eslint-disable vue/no-unused-vars -->
<template>
  <h2
    class="mt-0 tw-text-2xl tw-font-semibold tw-mb-2"
    style="color: var(--text-color)"
  >
    Gifts Management
  </h2>
  <div>
    <Card>
      <template #header>
        <div
          class="tw-flex tw-flex-wrap tw-justify-between tw-items-center tw-p-2 tw-gap-2"
        >
          <div class="tw-flex tw-flex-wrap tw-gap-2"></div>
          <InputText
            class="tw-w-full sm:tw-w-auto"
            type="text"
            v-model="podcastersTableFilter.global.value"
            placeholder="Global Search"
          />
        </div>
      </template>
      <template #content>
        <DataTable
          :value="podcasters"
          :paginator="true"
          :rows="10"
          :first="initFirst"
          @page="onChangePage"
          :filters="podcastersTableFilter"
          class="p-datatable-sm"
          :stripedRows="true"
          :globalFilterFields="['id', 'name', 'email', 'referrals_count']"
        >
          <Column field="id" header="ID" :sortable="true" style="width: 5%">
            <template #body="slotProps">
              <div
                class="tw-max-w-[200px] tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis tw-block"
                :title="slotProps.data.id"
              >
                {{ slotProps.data.id }}
              </div>
            </template>
          </Column>
          <Column
            field="name"
            header="Name"
            :sortable="true"
            style="width: 15%"
          >
            <template #body="slotProps">
              <div
                class="tw-max-w-[200px] tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis tw-block"
                :title="slotProps.data.name"
              >
                {{ slotProps.data.name }}
              </div>
            </template>
          </Column>
          <!-- Email -->
          <Column
            field="email"
            header="Email"
            :sortable="true"
            style="width: 20%"
          >
            <template #body="slotProps">
              <div
                class="tw-max-w-[200px] tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis tw-block"
                :title="slotProps.data.email"
              >
                {{ slotProps.data.email }}
              </div>
            </template>
          </Column>
          <!-- Created At -->
          <Column
            field="created_at"
            header="Created At"
            :sortable="true"
            style="width: 10%"
          >
            <template #body="slotProps">
              <div
                class="tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis"
                :title="slotProps.data.created_at"
              >
                {{ slotProps.data.created_at || "" }}
              </div>
            </template>
          </Column>
          <!-- Referrals Count -->
          <Column
            field="referrals_count"
            header="Referrals"
            :sortable="true"
            style="width: 8%"
          >
            <template #body="slotProps">
              <div
                class="tw-px-2 tw-py-1 tw-rounded tw-font-semibold tw-text-center tw-min-w-[2rem]"
              >
                {{ slotProps.data.referrals_count || 0 }}
              </div>
            </template>
          </Column>
          <!-- Rev Share -->
          <Column
            field="revshare"
            header="Revenue Share"
            :sortable="true"
            style="width: 15%"
          >
            <template #body="slotProps">
              <div
                class="tw-px-2 tw-py-1 tw-rounded tw-font-semibold tw-text-center tw-min-w-[2rem]"
              >
                {{ slotProps.data.revshare || 0 }}
              </div>
            </template>
          </Column>
          <!-- Actions -->
          <Column header="Actions" style="width: 16%">
            <template #body="slotProps">
              <div class="tw-flex tw-gap-1">
                <!-- Open user in podcasters page -->
                <Button
                  icon="pi pi-search"
                  class="p-button-sm p-button-outlined"
                  @click="goToPodcasterOverview(slotProps.data.email)"
                  title="Podcaster Overview"
                />
                <!-- Open Referrals List Modal -->
                <Button
                  icon="pi pi-users"
                  class="p-button-sm p-button-outlined"
                  @click="
                    openReferralsModal(
                      slotProps.data.podcaster_id,
                      slotProps.data.name
                    )
                  "
                  title="View Referrals"
                />
                <!-- Open Gifts Modal -->
                <Button
                  icon="pi pi-gift"
                  :class="[
                    'p-button-sm',
                    'p-button-outlined',
                    Number(slotProps.data.pending_amazon_gift_card_count) > 0 ||
                    Number(
                      slotProps.data.pending_revshare_5_percent_increase_count
                    ) > 0
                      ? 'p-button-success-outline'
                      : '',
                  ]"
                  @click="
                    openGiftsModal(
                      slotProps.data.podcaster_id,
                      slotProps.data.name
                    )
                  "
                  title="Manage Gifts"
                />
              </div>
            </template>
          </Column>
          <template #paginatorstart>
            <!-- Refresh List -->
            <Button
              type="button"
              icon="pi pi-refresh"
              @click="fetchPodcastersWithReferrals()"
              text
            />
          </template>
          <template #paginatorend>
            <div class="tw-flex tw-gap-2 tw-items-center">
              <download-csv :data="podcastersDownload" class="download-csv">
                <!-- Download CSS -->
                <Button type="button" icon="pi pi-download" text />
              </download-csv>
            </div>
          </template>
        </DataTable>
      </template>
    </Card>

    <!-- Referrals List Modal -->
    <Dialog
      v-model:visible="showReferralsModal"
      :header="`${selectedPodcasterName} - Referrals`"
      modal
      :style="{ width: '800px', maxWidth: '90vw' }"
      class="enhanced-modal"
    >
      <div class="modal-content">
        <!-- Loading State -->
        <div
          v-if="loadingReferrals"
          class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-12 tw-text-center"
          style="background: var(--surface-card)"
        >
          <div class="tw-mb-6">
            <i
              class="pi pi-spin pi-spinner tw-text-5xl"
              style="color: var(--primary-color)"
            ></i>
          </div>
          <h4
            class="tw-m-0 tw-mb-2 tw-text-xl tw-font-semibold"
            style="color: var(--text-color)"
          >
            Loading Referrals
          </h4>
          <p
            class="tw-m-0 tw-text-base tw-leading-6"
            style="color: var(--text-color-secondary)"
          >
            Please wait while we fetch the referral data...
          </p>
        </div>

        <!-- Empty State -->
        <div
          v-else-if="!referralsList?.length"
          class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-12 tw-text-center"
          style="background: var(--surface-card)"
        >
          <div class="tw-mb-6 tw-opacity-60">
            <i
              class="pi pi-users tw-text-5xl"
              style="color: var(--surface-400)"
            ></i>
          </div>
          <h4
            class="tw-m-0 tw-mb-2 tw-text-xl tw-font-semibold"
            style="color: var(--text-color)"
          >
            No Referrals Yet
          </h4>
          <p
            class="tw-m-0 tw-text-base tw-leading-6"
            style="color: var(--text-color-secondary)"
          >
            This podcaster hasn't made any referrals yet.
          </p>
        </div>

        <!-- Referrals List -->
        <div v-else style="background: var(--surface-card)">
          <div
            class="tw-pb-4 tw-border-b"
            style="
              padding-left: 1.5rem;
              border-bottom: 1px solid var(--surface-border);
              background: var(--surface-10);
            "
          >
            <div
              class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold"
              style="color: var(--text-color-secondary)"
            >
              <i class="pi pi-users" style="color: var(--primary-color)"></i>
              <span
                >{{ referralsList.length }} Referral{{
                  referralsList.length !== 1 ? "s" : ""
                }}</span
              >
            </div>
          </div>

          <div class="tw-max-h-96 tw-overflow-y-auto enhanced-scroll">
            <div
              v-for="(referral, index) in referralsList"
              :key="referral.podcaster_id"
              class="enhanced-card"
            >
              <!-- Referral Number -->
              <div
                class="tw-absolute tw-top-4 tw-right-4"
                style="color: var(--primary-color)"
              >
                {{ index + 1 }}
              </div>

              <!-- Referral Content -->
              <div class="tw-flex tw-items-start tw-gap-4 tw-mr-12">
                <div
                  class="tw-flex-shrink-0 tw-w-12 tw-h-12 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-xl tw-border-2"
                  style="
                    background: var(--surface-100);
                    border-color: var(--surface-200);
                  "
                >
                  <i class="pi pi-user" style="color: var(--primary-color)"></i>
                </div>
                <div class="tw-flex-1 tw-min-w-0">
                  <h5
                    class="tw-m-0 tw-mb-2 tw-text-lg tw-font-semibold tw-leading-tight"
                    style="color: var(--text-color)"
                  >
                    {{ referral.name }}
                  </h5>
                  <div
                    class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-2 tw-text-sm"
                  >
                    <div class="tw-flex tw-gap-2">
                      <span
                        class="tw-font-medium"
                        style="color: var(--text-color-secondary)"
                        >ID:</span
                      >
                      <span style="color: var(--text-color)">{{
                        referral.id
                      }}</span>
                    </div>
                    <div class="tw-flex tw-gap-2">
                      <span
                        class="tw-font-medium"
                        style="color: var(--text-color-secondary)"
                        >Email:</span
                      >
                      <span
                        style="color: var(--text-color)"
                        class="tw-break-all"
                        >{{ referral.email }}</span
                      >
                    </div>
                    <div class="tw-flex tw-gap-2">
                      <span
                        class="tw-font-medium"
                        style="color: var(--text-color-secondary)"
                        >Joined:</span
                      >
                      <span style="color: var(--text-color)">{{
                        new Date(referral.created_at)
                          .toISOString()
                          .split("T")[0]
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="tw-flex tw-justify-end tw-gap-3">
          <Button
            label="Close"
            icon="pi pi-times"
            @click="closeReferralsModal"
            class="p-button-outlined"
          />
        </div>
      </template>
    </Dialog>

    <!-- Gifts List Modal -->
    <Dialog
      v-model:visible="showGiftsModal"
      :header="`${selectedPodcasterName} - Gifts`"
      modal
      :style="{ width: '700px', maxWidth: '90vw' }"
      class="enhanced-modal"
    >
      <div class="modal-content">
        <!-- Loading State -->
        <div
          v-if="loadingGifts"
          class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-12 tw-text-center"
          style="background: var(--surface-card)"
        >
          <div class="tw-mb-6">
            <i
              class="pi pi-spin pi-spinner tw-text-5xl"
              style="color: var(--primary-color)"
            ></i>
          </div>
          <h4
            class="tw-m-0 tw-mb-2 tw-text-xl tw-font-semibold"
            style="color: var(--text-color)"
          >
            Loading Gifts
          </h4>
          <p
            class="tw-m-0 tw-text-base tw-leading-6"
            style="color: var(--text-color-secondary)"
          >
            Please wait while we fetch the gift data...
          </p>
        </div>

        <!-- Empty State -->
        <div
          v-else-if="!giftsList?.length"
          class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-12 tw-text-center"
          style="background: var(--surface-card)"
        >
          <div class="tw-mb-6 tw-opacity-60">
            <i
              class="pi pi-gift tw-text-5xl"
              style="color: var(--surface-400)"
            ></i>
          </div>
          <h4
            class="tw-m-0 tw-mb-2 tw-text-xl tw-font-semibold"
            style="color: var(--text-color)"
          >
            No Gifts Yet
          </h4>
          <p
            class="tw-m-0 tw-text-base tw-leading-6"
            style="color: var(--text-color-secondary)"
          >
            This podcaster hasn't received any gifts yet.
          </p>
        </div>

        <!-- Gifts List -->
        <div v-else style="background: var(--surface-card)">
          <div
            class="tw-pb-4 tw-border-b"
            style="
              padding-left: 1.5rem;
              border-bottom: 1px solid var(--surface-border);
              background: var(--surface-10);
            "
          >
            <div
              class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold"
              style="color: var(--text-color-secondary)"
            >
              <i class="pi pi-gift" style="color: var(--primary-color)"></i>
              <span
                >{{ giftsList.length }} Gift{{
                  giftsList.length !== 1 ? "s" : ""
                }}</span
              >
            </div>
          </div>

          <div class="tw-max-h-96 tw-overflow-y-auto enhanced-scroll">
            <div
              v-for="(gift, _) in giftsList"
              :key="gift.id"
              class="enhanced-card"
              :class="{ 'enhanced-card--verified': gift.is_gift_verified }"
            >
              <!-- Gift Status Badge -->
              <div class="tw-absolute tw-top-4 tw-right-4">
                <div
                  v-if="gift.is_gift_verified"
                  class="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-border"
                  style="
                    border-radius: var(--border-radius);
                    color: var(--primary-color);
                    border-color: var(--primary-color);
                  "
                >
                  <i class="pi pi-check-circle"></i>
                  <span>Approved</span>
                </div>
                <Button
                  v-else
                  label="Approve"
                  icon="pi pi-check"
                  class="p-button-sm p-button-success"
                  :loading="approvingGiftId === gift.id"
                  :disabled="approvingGiftId !== null"
                  @click="approveGift(gift.id, gift.podcaster_id)"
                />
              </div>

              <!-- Gift Content -->
              <div class="tw-flex tw-items-start tw-gap-4 tw-mr-24">
                <div
                  class="tw-flex-shrink-0 tw-w-12 tw-h-12 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-xl tw-border-2"
                  style="
                    background: var(--surface-100);
                    border-color: var(--surface-200);
                  "
                >
                  <i
                    :class="getGiftIcon(gift.gift_type)"
                    :style="{
                      color: gift.is_gift_verified
                        ? 'var(--primary-color)'
                        : 'var(--surface-500)',
                    }"
                  ></i>
                </div>
                <div class="tw-flex-1 tw-min-w-0">
                  <h5
                    class="tw-m-0 tw-mb-2 tw-text-lg tw-font-semibold tw-leading-tight"
                    style="color: var(--text-color)"
                  >
                    {{ getGiftTypeDisplay(gift.gift_type) }}
                  </h5>
                  <div
                    class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-2 tw-text-sm tw-mb-3"
                  >
                    <div class="tw-flex tw-gap-2">
                      <span
                        class="tw-font-medium"
                        style="color: var(--text-color-secondary)"
                        >Referrals:</span
                      >
                      <span style="color: var(--text-color)">{{
                        gift.referrals_count
                      }}</span>
                    </div>
                    <div class="tw-flex tw-gap-2">
                      <span
                        class="tw-font-medium"
                        style="color: var(--text-color-secondary)"
                        >Type:</span
                      >
                      <span
                        style="color: var(--primary-color); font-weight: bold"
                      >
                        {{
                          gift.is_gift_verified
                            ? getGiftType(
                                gift.gift_type,
                                selectedPodcaster,
                                true
                              )
                            : getGiftType(gift.gift_type, selectedPodcaster)
                        }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="tw-flex tw-justify-end tw-gap-3">
          <Button
            label="Close"
            icon="pi pi-times"
            @click="closeGiftsModal"
            class="p-button-outlined"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup>
import Card from "primevue/card";
import Button from "primevue/button";
import { API_BASE_URL } from "@/consts";
import { ref, computed, onBeforeMount } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { FilterMatchMode } from "primevue/api";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import axios from "axios";

// eslint-disable-next-line no-unused-vars
let tempFirst = 0;

const token = localStorage.getItem("podcastAdminToken");
const store = useStore();
const router = useRouter();

const showReferralsModal = ref(false);
const loadingReferrals = ref(false);
const referralsList = ref([]);
const selectedPodcasterName = ref("");
const showGiftsModal = ref(false);
const loadingGifts = ref(false);
const giftsList = ref([]);
const approvingGiftId = ref(null);
const initFirst = ref(Number(localStorage.getItem("first")));
const podcastersTableFilter = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
});

// Enum for gift types
const GiftTypes = Object.freeze({
  REVSHARE_5_PERCENT_INCREASE: "revshare-5-percent-increase",
  AMAZON_GIFT_CARD: "amazon-gift-card",
});

//Function to open podcasters page with search query for particular podcaster
const goToPodcasterOverview = (email) => {
  router.push({
    name: "PodcastersList",
    query: { search: email },
  });
};

//Function to open referrals modal for a specific podcaster
const openReferralsModal = async (podcasterId, podcasterName) => {
  selectedPodcasterName.value = podcasterName;
  showReferralsModal.value = true;
  loadingReferrals.value = true;
  referralsList.value = [];

  try {
    const response = await axios.get(
      API_BASE_URL + `/podcaster/referrals/${podcasterId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    referralsList.value = response.data.referrals
      ? response.data.referrals.sort(
          (a, b) => new Date(a.created_at) - new Date(b.created_at)
        )
      : [];
  } catch (error) {
    console.error("Error fetching referrals:", error);
    referralsList.value = [];
  } finally {
    loadingReferrals.value = false;
  }
};

// Function to close referrals modal and reset state
const closeReferralsModal = () => {
  showReferralsModal.value = false;
  referralsList.value = [];
  selectedPodcasterName.value = "";
};

// Function to open gifts modal for a specific podcaster
const openGiftsModal = async (podcasterId, podcasterName) => {
  selectedPodcasterName.value = podcasterName;
  showGiftsModal.value = true;
  loadingGifts.value = true;
  giftsList.value = [];

  try {
    const gifts = await fetchPodcasterGifts(podcasterId);
    giftsList.value = gifts.sort((a, b) => a.id - b.id);
  } catch (error) {
    console.error("Error fetching gifts:", error);
    giftsList.value = [];
  } finally {
    loadingGifts.value = false;
  }
};

// Function to close gifts modal and reset state
const closeGiftsModal = () => {
  showGiftsModal.value = false;
  giftsList.value = [];
  selectedPodcasterName.value = "";
  approvingGiftId.value = null;
};

// Function to fetch gifts for a specific podcaster
const fetchPodcasterGifts = async (podcasterId) => {
  try {
    const response = await axios.get(
      API_BASE_URL + `/podcaster-gifts/${podcasterId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data || [];
  } catch (error) {
    console.error("Error fetching gifts:", error);
    return [];
  }
};

// Function to approve a gift
const approveGift = async (giftId, podcasterId) => {
  approvingGiftId.value = giftId;

  try {
    await axios.post(
      API_BASE_URL + `/podcaster-gifts/approve/${giftId}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const updatedGifts = await fetchPodcasterGifts(podcasterId);
    giftsList.value = updatedGifts.sort((a, b) => a.id - b.id);
  } catch (error) {
    console.error("Error approving gift:", error);
  } finally {
    approvingGiftId.value = null;
    await fetchPodcastersWithReferrals();
  }
};

// Function to handle page change and store first index in localStorage
const onChangePage = (e) => {
  tempFirst = e.first;
  localStorage.removeItem("first");
};

// Data for CSV download
const podcastersDownload = computed(() => {
  if (podcasters.value.length > 0) {
    return podcasters.value.map((item) => ({
      ["id"]: item.id,
      ["name"]: item.name,
      ["email"]: item.email,
      ["created at"]: item.created_at,
      ["referrals"]: item.referrals_count || 0,
      ["pending gift cards"]: item.pending_amazon_gift_card_count || 0,
      ["approved gift cards"]: item.approved_amazon_gift_card_count || 0,
      ["pending revshare +5%"]:
        item.pending_revshare_5_percent_increase_count || 0,
      ["approved revshare +5%"]:
        item.approved_revshare_5_percent_increase_count || 0,
      ["current rev share"]: item.revshare || 0,
      ["pending rev share increase"]:
        (item.pending_revshare_5_percent_increase_count || 0) * 5,
    }));
  }
  return [];
});

// Variable to hold the list of podcasters
const podcasters = computed(() => {
  let tempPodcasters = store.getters.podcastersWithReferrals;
  if (!tempPodcasters) {
    tempPodcasters = [];
  }
  if (store.getters.shows.length > 0) {
    tempPodcasters = tempPodcasters.map((item) => ({
      ...item,
      rss_urls: store.getters.shows
        .filter((show) => show.podcaster_id === item.podcaster_id)
        .map((show) => ({ rss_url: show.rss_url, author: show.author })),
    }));
  }
  return tempPodcasters;
});

// Fetch podcasters with referrals and shows on component mount
const fetchPodcastersWithReferrals = async () => {
  store.dispatch("loadPodcastersWithReferrals", {
    token,
    fields: [
      "podcaster_id",
      "id",
      "name",
      "email",
      "created_at",
      "revshare",
      "referrals_count",
      "pending_amazon_gift_card_count",
      "approved_amazon_gift_card_count",
      "pending_revshare_5_percent_increase_count",
      "approved_revshare_5_percent_increase_count",
    ],
  });
};

// Find the selected podcaster object by podcaster_id for use in getGiftType
const selectedPodcaster = computed(() => {
  if (!showGiftsModal.value || !selectedPodcasterName.value) return null;
  let podcaster = podcasters.value.find(
    (p) => p.name === selectedPodcasterName.value
  );
  if (!podcaster && giftsList.value.length > 0) {
    const podcasterId = giftsList.value[0]?.podcaster_id;
    podcaster = podcasters.value.find((p) => p.podcaster_id === podcasterId);
  }
  return podcaster || null;
});

// Helper functions for gift display
const getGiftTypeDisplay = (giftType) => {
  switch (giftType) {
    case GiftTypes.AMAZON_GIFT_CARD:
      return "Amazon Gift Card";
    case GiftTypes.REVSHARE_5_PERCENT_INCREASE:
      return "Revenue Share +5%";
    default:
      return "Unknown Gift";
  }
};

// Function to get the appropriate icon class for the gift type
const getGiftIcon = (giftType) => {
  switch (giftType) {
    case GiftTypes.AMAZON_GIFT_CARD:
      return "pi pi-shopping-cart";
    case GiftTypes.REVSHARE_5_PERCENT_INCREASE:
      return "pi pi-percentage";
    default:
      return "pi pi-gift";
  }
};

// Function to get the gift type description based on type and podcaster
function getGiftType(type, podcaster, isApproved = false) {
  switch (type) {
    case GiftTypes.REVSHARE_5_PERCENT_INCREASE:
      if (isApproved) {
        return "Revenue Share +5%";
      }
      if (podcaster && typeof podcaster.revshare !== "undefined") {
        const nextRevshare = (+podcaster.revshare * 100 + 5) / 100;
        return `Revenue Share +5% Increase to ${nextRevshare}`;
      }
      return "Revenue Share +5% Increase";
    case GiftTypes.AMAZON_GIFT_CARD:
      return "Amazon Gift Card";
    default:
      return type;
  }
}

onBeforeMount(() => {
  fetchPodcastersWithReferrals();
});
</script>

<style scoped>
.p-datatable .p-datatable-thead > tr > th,
.p-datatable .p-datatable-tbody > tr > td {
  padding: 0.5rem;
  font-size: 0.9rem;
}

.p-card .p-card-body {
  padding: 0.5rem;
}

.p-dropdown,
.p-inputtext,
.p-button.p-button-sm {
  font-size: 0.9rem;
}

.p-button.p-button-sm {
  padding: 0.3rem 0.5rem;
}

.p-button-success-outline {
  border-color: var(--green-500) !important;
  color: var(--green-600) !important;
}

.enhanced-modal :deep(.p-dialog-header) {
  background: var(--surface-card);
  border-bottom: 1px solid var(--surface-border);
  padding: 1.5rem;
}

.enhanced-modal :deep(.p-dialog-content) {
  padding: 0;
  background: var(--surface-ground);
}

.enhanced-modal :deep(.p-dialog-footer) {
  background: var(--surface-card);
  border-top: 1px solid var(--surface-border);
  padding: 1rem 1.5rem;
}

.modal-content {
  min-height: 200px;
}

.enhanced-card {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: var(--border-radius);
  margin-bottom: 0.75rem;
  padding: 1.25rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.enhanced-card:hover {
  box-shadow: var(--shadow-2);
  transform: translateY(-1px);
}

.enhanced-card--verified {
  border-left: 4px solid var(--primary-color);
}

.enhanced-card:last-child {
  margin-bottom: 0;
}

.enhanced-scroll::-webkit-scrollbar {
  width: 6px;
}

.enhanced-scroll::-webkit-scrollbar-track {
  background: var(--surface-100);
  border-radius: 3px;
}

.enhanced-scroll::-webkit-scrollbar-thumb {
  background: var(--surface-300);
  border-radius: 3px;
}

.enhanced-scroll::-webkit-scrollbar-thumb:hover {
  background: var(--surface-400);
}

@media (max-width: 768px) {
  .enhanced-modal :deep(.p-dialog) {
    margin: 1rem;
    width: calc(100vw - 2rem) !important;
    max-width: none !important;
  }

  .enhanced-card .tw-mr-24 {
    margin-right: 0;
  }

  .enhanced-card .tw-mr-12 {
    margin-right: 0;
  }

  .enhanced-card .tw-flex {
    flex-direction: column;
    gap: 0.75rem;
  }

  .enhanced-card .tw-absolute {
    position: static;
    margin-bottom: 0.75rem;
    align-self: flex-end;
  }

  .enhanced-card .tw-grid-cols-2 {
    grid-template-columns: 1fr;
  }
}
</style>
